import { useNavigate, useRouterState } from '@tanstack/react-router'
import {
  Button,
  Header,
  Heading,
  Menu,
  MenuItem,
  MenuSection,
  MenuTrigger,
  Text,
} from '@react-spectrum/s2'
import { authClient } from '@client/auth-client'
import { trackAnalyticsEvent } from '@client/analytics'
import { APP_ROUTES } from '@shared/app-routes'
import { Route as RootRoute } from '@/routes/__root'
import { accountMenuStyles } from '@/styles/s2-macros'

const ADMIN_USERS_ROUTE = '/admin/users'
type HeaderButtonSize = 'S' | 'M'
type BetterAuthHeaderProps = {
  accountButtonSize?: HeaderButtonSize
}

function getImpersonatedBy(session: unknown): string | null {
  if (!session || typeof session !== 'object') {
    return null
  }

  const authSession = (session as { session?: { impersonatedBy?: unknown } })
    .session
  const impersonatedBy = authSession?.impersonatedBy

  return typeof impersonatedBy === 'string' && impersonatedBy.length > 0
    ? impersonatedBy
    : null
}

async function stopImpersonating() {
  trackAnalyticsEvent('impersonation_return_to_admin_clicked', {
    location: 'header',
  })
  await authClient.admin.stopImpersonating()
  window.location.href = ADMIN_USERS_ROUTE
}

async function signOutAndReload() {
  trackAnalyticsEvent('sign_out_clicked', {
    location: 'account_menu',
  })
  await authClient.signOut()
  window.location.reload()
}

export function ImpersonationBanner() {
  const { authSession } = RootRoute.useLoaderData()
  const { data: liveSession, isPending } = authClient.useSession()
  const session = isPending ? authSession : liveSession
  const impersonatedBy = getImpersonatedBy(session)

  if (!session?.user || !impersonatedBy) {
    return null
  }

  return (
    <div
      role="status"
      className="border-t border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-50"
    >
      <div className="container mx-auto flex max-w-7xl flex-col gap-2 px-6 py-2 text-sm sm:flex-row sm:items-center sm:justify-between">
        <span>
          You are impersonating {session.user.email || session.user.name}.
        </span>
        <Button
          type="button"
          variant="secondary"
          fillStyle="outline"
          size="S"
          onPress={stopImpersonating}
        >
          Return to admin
        </Button>
      </div>
    </div>
  )
}

export default function BetterAuthHeader({
  accountButtonSize = 'S',
}: BetterAuthHeaderProps = {}) {
  const { authSession } = RootRoute.useLoaderData()
  const { data: liveSession, isPending } = authClient.useSession()
  const session = isPending ? authSession : liveSession
  const navigate = useNavigate()
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })

  if (pathname === APP_ROUTES.login) {
    return null
  }

  if (session?.user) {
    const impersonatedBy = getImpersonatedBy(session)

    return (
      <MenuTrigger align="end">
        <Button
          variant="secondary"
          fillStyle="outline"
          size={accountButtonSize}
          aria-label="Open account menu"
          className="rounded-full"
        >
          {session.user.image ? (
            <span className="inline-flex h-6 w-6 overflow-hidden rounded-full">
              <img
                src={session.user.image}
                alt={`${session.user.name} avatar`}
                className="h-full w-full object-cover"
              />
            </span>
          ) : (
            <span className="text-sm font-bold text-foreground">
              {session.user.name.charAt(0).toUpperCase() || 'U'}
            </span>
          )}
        </Button>
        <Menu aria-label="Account actions" styles={accountMenuStyles}>
          <MenuSection>
            <Header>
              <Heading>{session.user.name}</Heading>
              <Text slot="description">{session.user.email}</Text>
            </Header>
            <MenuItem
              onAction={() => {
                trackAnalyticsEvent('account_menu_item_selected', {
                  item: 'dashboard',
                })
                navigate({ to: APP_ROUTES.dashboard.home })
              }}
            >
              Dashboard
            </MenuItem>
            <MenuItem
              onAction={() => {
                trackAnalyticsEvent('account_menu_item_selected', {
                  item: 'settings',
                })
                navigate({ to: APP_ROUTES.dashboard.settings })
              }}
            >
              Settings
            </MenuItem>
            {impersonatedBy ? (
              <MenuItem onAction={stopImpersonating}>Return to admin</MenuItem>
            ) : null}
          </MenuSection>
          <MenuSection aria-label="Session">
            <MenuItem onAction={signOutAndReload}>Sign out</MenuItem>
          </MenuSection>
        </Menu>
      </MenuTrigger>
    )
  }

  return (
    <Button
      type="button"
      onPress={() => {
        trackAnalyticsEvent('login_clicked', {
          location: 'header',
        })
        navigate({ to: APP_ROUTES.login })
      }}
    >
      Sign in
    </Button>
  )
}
